/*
  🦾 Custom utils by BoostEcom
  ➕ Shared JS utilities (lazy video, IntersectionObserver, play/pause icons, fullscreen, drawer accordion)
  🔒 Ne pas toucher — Utilisé par tous les blocs vidéo (UGC, Steps, Concept)
  ⚠️ Mise à jour effectuée le 20 mars 2026 par @Christopher Lasgi

  Problème : Chaque bloc vidéo avait son propre code dupliqué pour le lazy loading et les contrôles.
  Solution : Centraliser dans un fichier unique chargé 1 fois (defer) via theme.liquid.

  Import : <script src="{{ 'custom-utils_boostecom.js' | asset_url }}" defer></script>
  Dépendances : Aucune (vanilla JS)
*/

window.BoostEcom = window.BoostEcom || {};

/* ───────────────────────────────────────────────
   1. Lazy Video Loading (data-src → src)
   Used by: UGC, MiniUGC, Steps, Concept
   ─────────────────────────────────────────────── */

/**
 * Load a video source from data-src (source element) or data-video-src (video element).
 * Prevents double-loading via data-loaded flag.
 * @param {HTMLVideoElement} video
 */
BoostEcom.ensureVideoLoaded = function (video) {
  if (!video || video.dataset.loaded === 'true') return;

  // Pattern 1: <source data-src="..."> (UGC, Steps, Concept)
  var source = video.querySelector('source');
  if (source && source.dataset.src) {
    source.src = source.dataset.src;
    video.load();
    video.dataset.loaded = 'true';
    return;
  }

  // Pattern 2: <video data-video-src="..."> (MiniUGC)
  var src = video.getAttribute('data-video-src') || video.getAttribute('data-src');
  if (src && !video.src) {
    if (src.startsWith('//')) src = 'https:' + src;
    video.src = src;
    video.dataset.loaded = 'true';
  }

  // Poster from data attribute
  var poster = video.getAttribute('data-video-poster');
  if (poster && !video.getAttribute('poster')) {
    if (poster.startsWith('//')) poster = 'https:' + poster;
    video.setAttribute('poster', poster);
  }
};

/**
 * Load video source and attempt autoplay (muted).
 * Handles play() promise rejection gracefully.
 * @param {HTMLVideoElement} video
 */
BoostEcom.loadAndPlayVideo = function (video) {
  if (!video) return;

  BoostEcom.ensureVideoLoaded(video);

  video.muted = true;
  var p = video.play();
  if (p && typeof p.then === 'function') {
    p.catch(function () {
      video.muted = true;
      video.play().catch(function () {});
    });
  }
};

/* ───────────────────────────────────────────────
   2. IntersectionObserver for Video
   Used by: UGC, MiniUGC, Steps, Concept
   ─────────────────────────────────────────────── */

/**
 * Observe a video element and trigger callbacks on visibility change.
 * @param {HTMLVideoElement} video
 * @param {Object} [options]
 * @param {Function} [options.onEnter]  - Called when video enters viewport (default: ensureVideoLoaded)
 * @param {Function} [options.onLeave]  - Called when video leaves viewport (default: noop)
 * @param {boolean}  [options.autoplay] - If true, uses loadAndPlayVideo on enter and pause on leave
 * @param {number}   [options.threshold] - IntersectionObserver threshold (default: 0.25)
 * @param {string}   [options.rootMargin] - IntersectionObserver rootMargin (default: '200px 0px')
 * @returns {IntersectionObserver|null}
 */
BoostEcom.observeVideo = function (video, options) {
  if (!video || !('IntersectionObserver' in window)) {
    // Fallback: load immediately
    BoostEcom.ensureVideoLoaded(video);
    return null;
  }

  var opts = options || {};
  var threshold = opts.threshold !== undefined ? opts.threshold : 0.25;
  var rootMargin = opts.rootMargin || '200px 0px';
  var autoplay = opts.autoplay || false;

  var onEnter = opts.onEnter || (autoplay ? function () { BoostEcom.loadAndPlayVideo(video); } : function () { BoostEcom.ensureVideoLoaded(video); });
  var onLeave = opts.onLeave || (autoplay ? function () { video.pause(); } : function () {});

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (entry.isIntersecting) {
        onEnter();
      } else {
        onLeave();
      }
    });
  }, {
    threshold: threshold,
    rootMargin: rootMargin
  });

  io.observe(video);
  return io;
};

/* ───────────────────────────────────────────────
   3. Play/Pause & Mute Icon Toggle
   Used by: UGC, MiniUGC
   ─────────────────────────────────────────────── */

/**
 * Update play/pause icon visibility in a video card container.
 * Expects: .icon-play-btn, .icon-pause-btn, .icon-play-overlay, .icon-pause-overlay, .overlay-play
 * @param {HTMLElement} container - The .ugc-video-card or equivalent container
 * @param {boolean} playing
 */
BoostEcom.updatePlayIcons = function (container, playing) {
  if (!container) return;

  var playIconBtn = container.querySelector('.icon-play-btn');
  var pauseIconBtn = container.querySelector('.icon-pause-btn');
  var playIconOverlay = container.querySelector('.icon-play-overlay');
  var pauseIconOverlay = container.querySelector('.icon-pause-overlay');
  var overlay = container.querySelector('.overlay-play');

  if (playIconBtn) playIconBtn.style.display = playing ? 'none' : 'block';
  if (pauseIconBtn) pauseIconBtn.style.display = playing ? 'block' : 'none';
  if (playIconOverlay) playIconOverlay.style.display = playing ? 'none' : 'block';
  if (pauseIconOverlay) pauseIconOverlay.style.display = playing ? 'block' : 'none';
  if (overlay) {
    overlay.style.opacity = playing ? '0' : '1';
    overlay.style.pointerEvents = playing ? 'none' : 'auto';
  }
};

/**
 * Update mute/volume icon visibility.
 * Expects: .icon-volume, .icon-mute
 * @param {HTMLElement} container
 * @param {boolean} muted
 */
BoostEcom.updateMuteIcons = function (container, muted) {
  if (!container) return;

  var volumeIcon = container.querySelector('.icon-volume');
  var muteIcon = container.querySelector('.icon-mute');

  if (volumeIcon) volumeIcon.style.display = muted ? 'none' : 'block';
  if (muteIcon) muteIcon.style.display = muted ? 'block' : 'none';
};

/* ───────────────────────────────────────────────
   4. Fullscreen
   Used by: UGC, MiniUGC
   ─────────────────────────────────────────────── */

/**
 * Request fullscreen on an element with cross-browser support.
 * @param {HTMLElement} element
 */
BoostEcom.requestFullscreen = function (element) {
  if (!element) return;
  if (element.requestFullscreen) {
    element.requestFullscreen();
  } else if (element.webkitRequestFullscreen) {
    element.webkitRequestFullscreen();
  } else if (element.msRequestFullscreen) {
    element.msRequestFullscreen();
  }
};

/* ───────────────────────────────────────────────
   5. Video Controls Initializer
   Used by: UGC, MiniUGC
   Wires up all event listeners on a .ugc-video-card
   ─────────────────────────────────────────────── */

/**
 * Initialize video controls (play, pause, mute, fullscreen) on a card.
 * Prevents double-init via data-controls-initialized flag.
 * @param {HTMLElement} card - The .ugc-video-card container
 * @param {Object} [options]
 * @param {boolean} [options.observeLazy] - Observe for lazy loading (default: true)
 */
BoostEcom.initVideoControls = function (card, options) {
  if (!card || card.dataset.controlsInitialized === 'true') return;

  var opts = options || {};
  var video = card.querySelector('video');
  if (!video) return;

  var overlay = card.querySelector('.overlay-play');
  var playBtn = card.querySelector('.play-btn');
  var muteBtn = card.querySelector('.mute-btn');
  var fullscreenBtn = card.querySelector('.fullscreen-btn');

  function togglePlay() {
    BoostEcom.ensureVideoLoaded(video);
    if (video.paused) {
      video.play().then(function () {
        BoostEcom.updatePlayIcons(card, true);
      }).catch(function () {});
    } else {
      video.pause();
      BoostEcom.updatePlayIcons(card, false);
    }
  }

  function toggleMute() {
    video.muted = !video.muted;
    BoostEcom.updateMuteIcons(card, video.muted);
  }

  if (overlay) overlay.addEventListener('click', togglePlay);
  if (playBtn) playBtn.addEventListener('click', togglePlay);

  video.addEventListener('pause', function () { BoostEcom.updatePlayIcons(card, false); });
  video.addEventListener('play', function () { BoostEcom.updatePlayIcons(card, true); });

  if (muteBtn) muteBtn.addEventListener('click', toggleMute);

  if (fullscreenBtn) {
    fullscreenBtn.addEventListener('click', function () {
      BoostEcom.requestFullscreen(video);
    });
  }

  video.muted = true;
  BoostEcom.updatePlayIcons(card, false);
  BoostEcom.updateMuteIcons(card, video.muted);

  if (opts.observeLazy !== false) {
    BoostEcom.observeVideo(video);
  }

  card.dataset.controlsInitialized = 'true';
};

/* ───────────────────────────────────────────────
   6. Menu Drawer Accordion
   Ensures only one <details> is open at a time in the menu drawer.
   Originally in header-drawer.liquid — moved here for portability.
   ─────────────────────────────────────────────── */

BoostEcom.initDrawerAccordion = function () {
  var allAccordions = document.querySelectorAll('.menu-drawer details');
  if (!allAccordions.length) return;

  allAccordions.forEach(function (accordion) {
    accordion.addEventListener('toggle', function () {
      if (accordion.open) {
        allAccordions.forEach(function (other) {
          if (other !== accordion && other.open) {
            other.removeAttribute('open');
          }
        });
      }
    });
  });
};

document.addEventListener('DOMContentLoaded', function () {
  BoostEcom.initDrawerAccordion();
});

/*
  © 2026 CustomUtils. Tous droits réservés.
  Créé avec ❤️ par Christopher Lasgi pour les développeurs Shopify (OS 3.0).

  Mais ATTENTION :

  ⚠️ Pas pour les curieux. Ici on bosse.

  Tous les livrables dans ce service sont des créations originales protégées par l'écosystème « BoostEcom » (art. L111‑1 et suivants du Code de la propriété intellectuelle).

  En achetant mes services, tu obtiens une licence d'utilisation NON‑exclusive, NON‑transférable et NON‑cessionnable :

  ✔️ Usage illimité sur ta (tes) boutique(s) Shopify.
  ❌ Interdiction de revendre, copier, republier ou distribuer tout ou partie de ces livrables, même modifiés.
  ❌ Interdiction de créer un service "clone" ou d'en réutiliser la description (Shopify bannit la duplication).

  **Tout manquement déclenchera :**

  1. Plainte DMCA / retrait Shopify, conformément aux CGU (section 6, "contenu original").
  2. Facturation immédiate d'une pénalité équivalente à 3× le prix public.
  3. Action judiciaire en contrefaçon si nécessaire.
  4. Et l'envoi d'une armée de méchants robots à ta poursuite… (je plaisante 😅).

  Merci de respecter mon travail, créons plutôt ensemble des écosystèmes e‑commerce qui cartonnent ! ✌️
*/
